import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { viewportMatches } from '@system-dyg/shared/utils';
import { UiButtonComponent } from '../../atoms/button/button.component';
import { UiTagComponent } from '../../atoms/tag/tag.component';
import {
  UiPropertyProgressClip,
  UiPropertyProgressPlayEvent,
  UiPropertySummary,
} from './property-summary-card.types';

export type {
  UiPropertyAccountStatus,
  UiPropertyFeature,
  UiPropertyPaymentStatus,
  UiPropertyProgressClip,
  UiPropertyProgressPlayEvent,
  UiPropertySummary,
} from './property-summary-card.types';

/**
 * Organismo: ficha resumen de una propiedad o unidad.
 *
 * Incluye imagen, datos, saldo, pagos y carrusel de avance de obra.
 * Es presentacional: emite acciones y no conoce API ni rutas.
 */
@Component({
  selector: 'ui-property-summary-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiButtonComponent, UiTagComponent],
  templateUrl: './property-summary-card.component.html',
  styleUrl: './property-summary-card.component.scss',
  host: {
    class: 'ui-property-summary-card-host',
    '[class.ui-property-summary-card-host--single-progress]':
      'singleProgressViewport()',
  },
})
export class UiPropertySummaryCardComponent {
  readonly property = input.required<UiPropertySummary>();
  readonly statementLabel = input('Estado de cuenta');
  readonly registerPaymentLabel = input('Registrar pago');
  readonly downloadReceiptLabel = input('Descargar recibo');
  readonly viewDepartmentLabel = input('Ver departamento');

  readonly statementClick = output<void>();
  readonly registerPaymentClick = output<void>();
  readonly downloadReceiptClick = output<void>();
  readonly viewDepartmentClick = output<void>();
  readonly progressPlay = output<UiPropertyProgressPlayEvent>();

  constructor() {
    effect(() => {
      const last = this.progressLastIndex();
      if (this.progressIndex() > last) {
        this.progressIndex.set(last);
      }
    });
  }

  private readonly isMobileViewport = viewportMatches('(max-width: 767.98px)');

  /** Tableta 1024–1365: un video. Escritorio y celular conservan dos. */
  protected readonly singleProgressViewport = viewportMatches(
    '(min-width: 1024px) and (max-width: 1365.98px)',
  );
  private readonly progressVisibleCount = computed(() =>
    this.singleProgressViewport() ? 1 : 2,
  );
  protected readonly progressIndex = signal(0);

  protected onPreviewClick(event: Event): void {
    if (!this.isMobileViewport()) return;
    const target = event.target;
    if (target instanceof Element && target.closest('ui-button')) return;
    this.viewDepartmentClick.emit();
  }
  private progressSwipeX = 0;
  private progressSwipeActive = false;

  protected readonly isPaid = computed(
    () => (this.property().accountStatus ?? 'with-balance') === 'paid',
  );

  protected readonly isOverdue = computed(
    () =>
      !this.isPaid() &&
      (this.property().paymentStatus ?? 'current') === 'overdue',
  );

  protected readonly displayBalance = computed(() =>
    this.isPaid() ? '$0.00' : this.property().balanceAmount,
  );

  protected readonly overduePaymentsLabel = computed(() => {
    const count = this.property().overduePaymentsCount || 3;
    return count === 1 ? '1 pago atrasado' : `${count} pagos atrasados`;
  });

  protected readonly overdueAmount = computed(
    () => this.property().overdueAmount || '$75,000.00',
  );

  protected canPrevProgress(): boolean {
    return this.progressIndex() > 0;
  }

  protected canNextProgress(): boolean {
    return this.progressIndex() < this.progressLastIndex();
  }

  protected shiftProgress(delta: number): void {
    const last = this.progressLastIndex();
    const next = Math.min(last, Math.max(0, this.progressIndex() + delta));
    this.progressIndex.set(next);
  }

  protected onProgressSwipeStart(event: PointerEvent): void {
    this.progressSwipeActive =
      window.matchMedia('(max-width: 767.98px)').matches &&
      !(event.target as HTMLElement).closest('button');
    this.progressSwipeX = event.clientX;
  }

  protected onProgressSwipeEnd(event: PointerEvent): void {
    if (!this.progressSwipeActive) return;
    this.progressSwipeActive = false;
    const delta = event.clientX - this.progressSwipeX;
    if (Math.abs(delta) < 40) return;
    this.shiftProgress(delta < 0 ? 1 : -1);
  }

  protected onProgressThumbReady(event: Event): void {
    const video = event.target as HTMLVideoElement;
    if (video.currentTime === 0) {
      video.currentTime = 0.1;
    }
  }

  protected openProgressVideo(clip: UiPropertyProgressClip): void {
    this.progressPlay.emit({
      clip,
      development: this.property().development,
    });
  }

  private progressLastIndex(): number {
    return Math.max(
      0,
      this.property().progressClips.length - this.progressVisibleCount(),
    );
  }
}
